import { createApp } from 'vue'
import App from './App.vue'
import router from './router/index.ts'

import Aura from '@primeuix/themes/aura'
import { definePreset } from '@primeuix/themes'
import PrimeVue from 'primevue/config'
import ConfirmationService from 'primevue/confirmationservice'
import ToastService from 'primevue/toastservice'
import Tooltip from 'primevue/tooltip'
import { createPinia } from 'pinia'

import '@/assets/tailwind.css'
import '@/assets/styles.scss'
import { usePostHog } from '@/composables/use-posthog.ts'
import * as Sentry from '@sentry/vue'
import { scrubSentryEvent } from '@/utils/sentry-scrubber'
import { trackUncaughtErrors } from '@/utils/error-events'

const Noir = definePreset(Aura, {
  semantic: {
    primary: {
      50: '{stone.50}',
      100: '{stone.100}',
      200: '{stone.200}',
      300: '{stone.300}',
      400: '{stone.400}',
      500: '{stone.500}',
      600: '{stone.600}',
      700: '{stone.700}',
      800: '{stone.800}',
      900: '{stone.900}',
      950: '{stone.950}',
    },
    colorScheme: {
      light: {
        primary: {
          color: '{stone.900}',
          inverseColor: '#ffffff',
          hoverColor: '{stone.800}',
          activeColor: '{stone.700}',
        },
        highlight: {
          background: '{stone.800}',
          focusBackground: '{stone.600}',
          color: '#ffffff',
          focusColor: '#ffffff',
        },
      },
      dark: {
        primary: {
          color: '{stone.50}',
          inverseColor: '{stone.950}',
          hoverColor: '{stone.100}',
          activeColor: '{stone.200}',
        },
        highlight: {
          background: 'rgba(250, 250, 250, .16)',
          focusBackground: 'rgba(250, 250, 250, .24)',
          color: 'rgba(255,255,255,.87)',
          focusColor: 'rgba(255,255,255,.87)',
        },
      },
    },
  },
})

const app = createApp(App)

app.use(createPinia())
app.use(router)
app.use(PrimeVue, {
  theme: {
    preset: Noir,
    options: {
      darkModeSelector: '.app-dark'
    }
  }
})
app.use(ToastService)
app.use(ConfirmationService)
app.directive('tooltip', Tooltip)

const { initPostHog, trackEvent } = usePostHog()
initPostHog()

Sentry.init({
  app,
  dsn: 'https://a303ee44e19c33c7aa9db83c6dfee352@o4509955956736000.ingest.us.sentry.io/4509968018833408',
  environment: import.meta.env.VITE_VERCEL_ENV || 'development',
  release: import.meta.env.VITE_VERCEL_GIT_COMMIT_REF && import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA
    ? `${import.meta.env.VITE_VERCEL_GIT_COMMIT_REF}-${import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA.slice(0, 7)}`
    : 'local',

  enabled: true,
  sendDefaultPii: false,
  maxBreadcrumbs: 0,
  beforeBreadcrumb: () => null,
  beforeSend: scrubSentryEvent,
  beforeSendTransaction: scrubSentryEvent,
  tracesSampleRate: import.meta.env.VITE_VERCEL_ENV === 'production' ? 0.1 : 1.0,
})

trackUncaughtErrors(trackEvent)

app.mount('#app')
